import React, {
  useRef, useEffect, useState, useCallback,
} from 'react';
import {
  Box, Paper, IconButton, Tooltip, Typography, Slider, FormControlLabel, Switch,
} from '@mui/material';
import {
  ZoomIn as ZoomInIcon, ZoomOut as ZoomOutIcon,
  Fullscreen as FullscreenIcon, Visibility as VisibilityIcon, VisibilityOff as VisibilityOffIcon,
  GpsFixed as CenterIcon,
} from '@mui/icons-material';

// Minimap component for flow navigation
function FlowMinimap({
  nodes = [],
  edges = [],
  viewport,
  onViewportChange,
  canvasRef,
  isVisible = true,
  onToggleVisibility,
  position = 'bottom-right', // 'bottom-right', 'bottom-left', 'top-right', 'top-left'
}) {
  const minimapRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [minimapScale, setMinimapScale] = useState(0.1);
  const [showLabels, setShowLabels] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Calculate bounds of all nodes
  const getFlowBounds = useCallback(() => {
    if (nodes.length === 0) {
      return {
        minX: 0, minY: 0, maxX: 1000, maxY: 1000,
      };
    }

    const positions = nodes.map((node) => node.position);
    const minX = Math.min(...positions.map((p) => p.x));
    const minY = Math.min(...positions.map((p) => p.y));
    const maxX = Math.max(...positions.map((p) => p.x + 200)); // Assuming node width
    const maxY = Math.max(...positions.map((p) => p.y + 100)); // Assuming node height

    return {
      minX, minY, maxX, maxY,
    };
  }, [nodes]);

  // Calculate minimap dimensions
  const getMinimapDimensions = useCallback(() => {
    const bounds = getFlowBounds();
    const padding = 50;

    return {
      width: Math.max(300, (bounds.maxX - bounds.minX + padding * 2) * minimapScale),
      height: Math.max(200, (bounds.maxY - bounds.minY + padding * 2) * minimapScale),
      offsetX: -bounds.minX + padding,
      offsetY: -bounds.minY + padding,
    };
  }, [getFlowBounds, minimapScale]);

  // Convert canvas coordinates to minimap coordinates
  const canvasToMinimap = useCallback((canvasX, canvasY) => {
    const dims = getMinimapDimensions();
    return {
      x: (canvasX + dims.offsetX) * minimapScale,
      y: (canvasY + dims.offsetY) * minimapScale,
    };
  }, [getMinimapDimensions, minimapScale]);

  // Convert minimap coordinates to canvas coordinates
  const minimapToCanvas = useCallback((minimapX, minimapY) => {
    const dims = getMinimapDimensions();
    return {
      x: minimapX / minimapScale - dims.offsetX,
      y: minimapY / minimapScale - dims.offsetY,
    };
  }, [getMinimapDimensions, minimapScale]);

  // Handle minimap click/drag
  const handleMinimapInteraction = useCallback((event) => {
    if (!minimapRef.current || !onViewportChange) return;

    const rect = minimapRef.current.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    if (event.type === 'mousedown') {
      setIsDragging(true);
      setDragStart({ x, y });
    } else if (event.type === 'mousemove' && isDragging) {
      const canvasPos = minimapToCanvas(x, y);
      onViewportChange({
        x: canvasPos.x - viewport.width / 2,
        y: canvasPos.y - viewport.height / 2,
        zoom: viewport.zoom,
      });
    } else if (event.type === 'mouseup') {
      setIsDragging(false);
    } else if (event.type === 'click' && !isDragging) {
      // Center viewport on clicked position
      const canvasPos = minimapToCanvas(x, y);
      onViewportChange({
        x: canvasPos.x - viewport.width / 2,
        y: canvasPos.y - viewport.height / 2,
        zoom: viewport.zoom,
      });
    }
  }, [isDragging, minimapToCanvas, onViewportChange, viewport, dragStart]);

  // Handle zoom changes
  const handleZoomChange = useCallback((event, newValue) => {
    setMinimapScale(newValue / 100);
  }, []);

  // Center on flow
  const handleCenterFlow = useCallback(() => {
    if (!onViewportChange) return;

    const bounds = getFlowBounds();
    const centerX = (bounds.minX + bounds.maxX) / 2;
    const centerY = (bounds.minY + bounds.maxY) / 2;

    onViewportChange({
      x: centerX - viewport.width / 2,
      y: centerY - viewport.height / 2,
      zoom: viewport.zoom,
    });
  }, [getFlowBounds, onViewportChange, viewport]);

  // Toggle fullscreen
  const handleToggleFullscreen = useCallback(() => {
    setIsFullscreen(!isFullscreen);
  }, [isFullscreen]);

  // Get node color based on type
  const getNodeColor = useCallback((nodeType) => {
    const colors = {
      start: '#4caf50',
      end: '#f44336',
      'ai-agent': '#2196f3',
      'api-call': '#ff9800',
      'code-executor': '#9c27b0',
      conditional: '#ff5722',
      loop: '#795548',
      email: '#00bcd4',
      sms: '#4caf50',
      form: '#ffc107',
      'web-crawler': '#795548',
      connector: '#9c27b0',
      policy: '#2e7d32',
      default: '#757575',
    };
    return colors[nodeType] || colors.default;
  }, []);

  // Get edge color based on type
  const getEdgeColor = useCallback((edgeType) => {
    const colors = {
      'edge-success': '#4caf50',
      'edge-error': '#f44336',
      'edge-true': '#2196f3',
      'edge-false': '#ff9800',
      'edge-loop': '#9c27b0',
      'edge-complete': '#4caf50',
      default: '#757575',
    };
    return colors[edgeType] || colors.default;
  }, []);

  if (!isVisible) {
    return (
      <Tooltip title="Show Minimap">
        <IconButton
          onClick={onToggleVisibility}
          sx={{
            position: 'absolute',
            [position.includes('right') ? 'right' : 'left']: 16,
            [position.includes('bottom') ? 'bottom' : 'top']: 16,
            zIndex: 1000,
            bgcolor: 'background.paper',
            boxShadow: 2,
            border: 1,
            borderColor: 'divider',
            '&:hover': {
              bgcolor: 'background.paper',
              boxShadow: 4,
            },
          }}
        >
          <VisibilityIcon />
        </IconButton>
      </Tooltip>
    );
  }

  const dims = getMinimapDimensions();
  const viewportRect = {
    x: canvasToMinimap(viewport.x, viewport.y).x,
    y: canvasToMinimap(viewport.x, viewport.y).y,
    width: viewport.width * minimapScale,
    height: viewport.height * minimapScale,
  };

  return (
    <Paper
      ref={minimapRef}
      sx={{
        position: isFullscreen ? 'fixed' : 'absolute',
        [position.includes('right') ? 'right' : 'left']: isFullscreen ? 0 : 16,
        [position.includes('bottom') ? 'bottom' : 'top']: isFullscreen ? 0 : 16,
        width: isFullscreen ? '100vw' : Math.min(350, dims.width + 80),
        height: isFullscreen ? '100vh' : Math.min(250, dims.height + 80),
        zIndex: 1000,
        p: 1,
        bgcolor: 'background.paper',
        boxShadow: 4,
        border: 1,
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        margin: 1,
      }}
      onMouseDown={handleMinimapInteraction}
      onMouseMove={handleMinimapInteraction}
      onMouseUp={handleMinimapInteraction}
      onClick={handleMinimapInteraction}
    >
      {/* Header */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 1,
        pb: 1,
        borderBottom: 1,
        borderColor: 'divider',
      }}
      >
        <Typography variant="caption" sx={{ fontWeight: 600 }}>
          Flow Minimap
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Center on Flow">
            <IconButton size="small" onClick={handleCenterFlow}>
              <CenterIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}>
            <IconButton size="small" onClick={handleToggleFullscreen}>
              <FullscreenIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Hide Minimap">
            <IconButton size="small" onClick={onToggleVisibility}>
              <VisibilityOffIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Minimap Content */}
      <Box sx={{
        flex: 1,
        position: 'relative',
        overflow: 'hidden',
        bgcolor: 'grey.50',
        borderRadius: 1,
        border: 1,
        borderColor: 'grey.300',
      }}
      >
        {/* Flow Content */}
        <Box
          sx={{
            position: 'absolute',
            width: dims.width,
            height: dims.height,
            transform: `scale(${minimapScale})`,
            transformOrigin: 'top left',
          }}
        >
          {/* Edges */}
          {edges.map((edge) => {
            const sourceNode = nodes.find((n) => n.id === edge.source);
            const targetNode = nodes.find((n) => n.id === edge.target);

            if (!sourceNode || !targetNode) return null;

            const sourceX = sourceNode.position.x + dims.offsetX + 100; // Center of node
            const sourceY = sourceNode.position.y + dims.offsetY + 50;
            const targetX = targetNode.position.x + dims.offsetX + 100;
            const targetY = targetNode.position.y + dims.offsetY + 50;

            return (
              <Box
                key={edge.id}
                sx={{
                  position: 'absolute',
                  left: sourceX,
                  top: sourceY,
                  width: Math.sqrt((targetX - sourceX) ** 2 + (targetY - sourceY) ** 2),
                  height: 2,
                  bgcolor: getEdgeColor(edge.type),
                  transformOrigin: 'left center',
                  transform: `rotate(${Math.atan2(targetY - sourceY, targetX - sourceX)}rad)`,
                  opacity: 0.7,
                }}
              />
            );
          })}

          {/* Nodes */}
          {nodes.map((node) => {
            const nodeX = node.position.x + dims.offsetX;
            const nodeY = node.position.y + dims.offsetY;

            return (
              <Box
                key={node.id}
                sx={{
                  position: 'absolute',
                  left: nodeX,
                  top: nodeY,
                  width: 200,
                  height: 100,
                  bgcolor: getNodeColor(node.type),
                  borderRadius: 1,
                  border: 1,
                  borderColor: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  '&:hover': {
                    opacity: 0.8,
                  },
                }}
              >
                {showLabels && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'white',
                      fontWeight: 600,
                      textAlign: 'center',
                      fontSize: '0.6rem',
                      lineHeight: 1,
                    }}
                  >
                    {node.label || node.type}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>

        {/* Viewport Indicator */}
        <Box
          sx={{
            position: 'absolute',
            left: viewportRect.x,
            top: viewportRect.y,
            width: viewportRect.width,
            height: viewportRect.height,
            border: 2,
            borderColor: 'primary.main',
            bgcolor: 'primary.lighter',
            opacity: 0.3,
            pointerEvents: 'none',
            borderRadius: 0.5,
          }}
        />
      </Box>

      {/* Controls */}
      <Box sx={{
        mt: 1, display: 'flex', flexDirection: 'column', gap: 1,
      }}
      >
        {/* Zoom Control */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ZoomOutIcon fontSize="small" />
          <Slider
            value={minimapScale * 100}
            onChange={handleZoomChange}
            min={5}
            max={50}
            step={5}
            size="small"
            sx={{ flex: 1 }}
          />
          <ZoomInIcon fontSize="small" />
        </Box>

        {/* Options */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <FormControlLabel
            control={(
              <Switch
                checked={showLabels}
                onChange={(e) => setShowLabels(e.target.checked)}
                size="small"
              />
            )}
            label={(
              <Typography variant="caption">
                Labels
              </Typography>
            )}
            sx={{ m: 0 }}
          />
          <Typography variant="caption" color="text.secondary">
            {nodes.length}
            {' '}
            nodes,
            {edges.length}
            {' '}
            edges
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
}

export default FlowMinimap;
